import React, { useEffect, useState, useRef } from 'react';
import { db } from './firebaseConfig';
import { doc, getDoc, getDocFromCache, addDoc, updateDoc, collection } from 'firebase/firestore';
import { useParams, useLocation } from 'react-router-dom';
import { matrixPayloadToPlainText } from './matrixAnswerFormat';
import { useInterviewRecorder, formatRecordingDuration } from './useInterviewRecorder';
import { uploadInterviewAudio, formatUploadError } from './uploadInterviewAudio';
import { ensureResponderAuth } from './ensureResponderAuth';

function isMatrixIncomplete(q, answer) {
  const rows = q.matrixRows || [];
  if (rows.length === 0) return true;
  if (!answer || typeof answer !== 'object' || Array.isArray(answer)) return true;
  for (let ri = 0; ri < rows.length; ri++) {
    const v = answer[ri] ?? answer[String(ri)];
    if (v === undefined || v === null || v === '') return true;
  }
  return false;
}

function cannotProceedRequired(q, answer) {
  if (!q.required) return false;
  if (q.type === 'matrix') {
    return isMatrixIncomplete(q, answer);
  }
  if (q.type === 'checkbox') {
    return !Array.isArray(answer) || answer.length === 0;
  }
  return !answer || (typeof answer === 'string' && answer.trim() === '');
}

function buildMatrixResultPayload(q, answer) {
  const rows = q.matrixRows || [];
  const cols = q.options || [];
  const byRow = [];
  for (let ri = 0; ri < rows.length; ri++) {
    const colIdx = answer?.[ri] ?? answer?.[String(ri)];
    const has = colIdx !== undefined && colIdx !== null && colIdx !== '';
    const col = has ? cols[colIdx] : null;
    const colText = col && (typeof col === 'string' ? col : col.text ?? '');
    byRow.push({
      rowLabel: rows[ri] ?? '',
      columnIndex: has ? colIdx : null,
      columnLabel: colText || '',
    });
  }
  return { matrix: true, rows: byRow };
}

const ResponderQuestionario = () => {
  const { id } = useParams();
  const location = useLocation();
  const [questionario, setQuestionario] = useState(null);
  const [respostas, setRespostas] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [surveyStarted, setSurveyStarted] = useState(false);
  /** Índices de perguntas não visitadas por causa do "pular para" — não exigem resposta se obrigatórias */
  const skippedByJumpRef = useRef(new Set());

  // Detecta se está em modo preview
  const isPreviewMode = new URLSearchParams(location.search).get('preview') === 'true';
  const shouldRecordAudio = !isPreviewMode && surveyStarted;
  const { isRecording, recordingError, elapsedSeconds, stopRecording } =
    useInterviewRecorder(shouldRecordAudio);

  useEffect(() => {
    let cancelled = false;
    const docRef = doc(db, 'questionarios', id);

    const applyQuestionario = (data) => {
      if (cancelled || !data?.questions) return;
      setQuestionario(data);
      setRespostas(
        data.questions.map((q) =>
          q.type === 'matrix' ? {} : q.type === 'checkbox' ? [] : ''
        )
      );
    };

    const fetchQuestionario = async () => {
      // 1) Cache local (IndexedDB) — primeira pintura rápida em visitas repetidas
      try {
        const fromCache = await getDocFromCache(docRef);
        if (fromCache.exists()) applyQuestionario(fromCache.data());
      } catch {
        // sem cache ainda
      }

      const docSnap = await getDoc(docRef);
      if (cancelled) return;
      if (docSnap.exists()) {
        applyQuestionario(docSnap.data());
      } else {
        console.log('Questionário não encontrado.');
      }
    };

    fetchQuestionario();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleChange = (index, value) => {
    const newRespostas = [...respostas];
    newRespostas[index] = value;
    setRespostas(newRespostas);
  };

  const handleMatrixCellChange = (questionIndex, rowIndex, columnIndex) => {
    const newRespostas = [...respostas];
    const prev = newRespostas[questionIndex];
    const base =
      prev && typeof prev === 'object' && !Array.isArray(prev) ? { ...prev } : {};
    base[String(rowIndex)] = columnIndex;
    newRespostas[questionIndex] = base;
    setRespostas(newRespostas);
  };

  const handleCheckboxChange = (questionIndex, optionText, checked) => {
    setRespostas((prev) => {
      const next = [...prev];
      const cur = Array.isArray(next[questionIndex]) ? [...next[questionIndex]] : [];
      if (checked) {
        if (!cur.includes(optionText)) cur.push(optionText);
      } else {
        const i = cur.indexOf(optionText);
        if (i !== -1) cur.splice(i, 1);
      }
      next[questionIndex] = cur;
      return next;
    });
  };

  const handleNext = () => {
    const currentQ = questionario.questions[currentQuestion];
    const currentAnswer = respostas[currentQuestion];
    
    if (cannotProceedRequired(currentQ, currentAnswer)) {
      alert('Esta pergunta é obrigatória. Por favor, responda antes de continuar.');
      return;
    }
    
    if (currentQ.type === 'radio' && currentAnswer) {
      const opts = currentQ.options || [];
      const selectedOptionIndex = opts.findIndex((opt) => {
        const optText = typeof opt === 'string' ? opt : opt.text;
        return optText === currentAnswer;
      });
      
      if (selectedOptionIndex !== -1) {
        const selectedOption = opts[selectedOptionIndex];
        const skipTo = typeof selectedOption === 'string' ? null : selectedOption.skipTo;
        
        if (skipTo === 'end') {
          for (let i = currentQuestion + 1; i < questionario.questions.length; i++) {
            skippedByJumpRef.current.add(i);
          }
          handleSubmit();
          return;
        } else if (skipTo !== null && skipTo !== undefined) {
          for (let i = currentQuestion + 1; i < skipTo; i++) {
            skippedByJumpRef.current.add(i);
          }
          skippedByJumpRef.current.delete(skipTo);
          setCurrentQuestion(skipTo);
          return;
        }
      }
    }
    
    // Próxima pergunta normalmente
    if (currentQuestion < questionario.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleCancel = () => {
    if (window.confirm('Tem certeza que deseja cancelar? Suas respostas serão perdidas.')) {
      window.location.reload();
    }
  };

  const refreshQuestionario = async () => {
    const snap = await getDoc(doc(db, 'questionarios', id));
    if (snap.exists()) {
      const data = snap.data();
      setQuestionario((prev) => ({ ...prev, ...data }));
      return data;
    }
    return null;
  };

  const handleStartSurvey = async () => {
    if (!isPreviewMode) {
      try {
        const latest = await refreshQuestionario();
        if (latest?.suspenso === true) {
          return;
        }
      } catch (err) {
        console.warn('Não foi possível verificar o status da pesquisa:', err);
      }
      try {
        await ensureResponderAuth();
      } catch (err) {
        console.warn('Login anônimo indisponível para envio de áudio:', err);
      }
    }
    setSurveyStarted(true);
  };

  const handleSubmit = async () => {
    const currentQ = questionario.questions[currentQuestion];
    const currentAnswer = respostas[currentQuestion];
    
    // Se está em modo preview, apenas mostra um alerta e não salva
    if (isPreviewMode) {
      alert('🔍 Modo Preview: Este é apenas um preview do questionário. Nenhuma resposta será salva.');
      return;
    }

    try {
      const latest = await refreshQuestionario();
      if (latest?.suspenso === true) {
        setSurveyStarted(false);
        alert('Esta pesquisa foi suspensa e não está mais recebendo respostas.');
        return;
      }
    } catch (err) {
      console.warn('Não foi possível verificar o status da pesquisa:', err);
    }
    
    if (cannotProceedRequired(currentQ, currentAnswer)) {
      alert('Esta pergunta é obrigatória. Por favor, responda antes de finalizar.');
      return;
    }

    const perguntasObrigatoriasNaoRespondidas = questionario.questions.filter(
      (q, index) =>
        cannotProceedRequired(q, respostas[index]) &&
        !skippedByJumpRef.current.has(index)
    );

    if (perguntasObrigatoriasNaoRespondidas.length > 0) {
      alert(`Ainda existem ${perguntasObrigatoriasNaoRespondidas.length} pergunta(s) obrigatória(s) não respondida(s). Por favor, revise suas respostas.`);
      return;
    }

    if (!window.confirm('Deseja enviar suas respostas?')) {
      return;
    }

    setIsSubmitting(true);

    try {
      const { blob: audioBlob, duration: audioDuration, mimeType: audioMimeType } =
        shouldRecordAudio ? await stopRecording() : { blob: null, duration: 0, mimeType: null };

      const ip = await getUserIP();
      const geoLocation = await getUserLocation();
      
      const resultData = {
        questionarioId: id,
        answers: [
          {
            ativo: true,
            id: 1,
            nome: (() => {
              const s = String(questionario?.nome ?? questionario?.name ?? '').trim();
              return s || 'Pesquisa final';
            })(),
            questions: questionario.questions.map((q, index) => {
              const raw = respostas[index];
              if (q.type === 'matrix') {
                const matrixPayload = buildMatrixResultPayload(q, raw);
                return {
                  tituloBloco: q.tituloBloco ?? '',
                  question: q.question,
                  questionType: 'matrix',
                  options: [matrixPayloadToPlainText(matrixPayload)],
                  matrixPayload,
                };
              }
              if (q.type === 'checkbox') {
                const selected = Array.isArray(raw) ? raw.filter((s) => s != null && String(s).trim() !== '') : [];
                return {
                  tituloBloco: q.tituloBloco ?? '',
                  question: q.question,
                  questionType: 'checkbox',
                  options: selected,
                };
              }
              return {
                tituloBloco: q.tituloBloco ?? '',
                question: q.question,
                options: [typeof raw === 'string' ? raw : ''],
              };
            }),
          },
        ],
        timestamp: new Date().toISOString(),
        origem: 'web',
        ip,
        location: {
          latitude: geoLocation.latitude,
          longitude: geoLocation.longitude,
          city: geoLocation.city,
          state: geoLocation.state,
          country: geoLocation.country,
          address: geoLocation.address
        },
      };

      if (shouldRecordAudio && audioBlob) {
        resultData.audioDuration = audioDuration;
      }
  
      const docRef = await addDoc(collection(db, 'results'), resultData);

      if (shouldRecordAudio && audioBlob && audioBlob.size > 0) {
        try {
          await ensureResponderAuth();
          const audioUrl = await uploadInterviewAudio(id, docRef.id, audioBlob, audioMimeType);
          await updateDoc(docRef, { audioUrl });
        } catch (uploadError) {
          console.error('Erro ao enviar áudio:', uploadError);
          alert(`Respostas salvas, mas o áudio não pôde ser enviado. ${formatUploadError(uploadError)}`);
        }
      } else if (shouldRecordAudio && (!audioBlob || audioBlob.size === 0)) {
        console.warn('Gravação vazia no dispositivo — resposta salva sem áudio.');
        alert(
          'Respostas salvas, mas o áudio ficou vazio neste aparelho. Tente usar Chrome/Safari atualizado e permitir o microfone.'
        );
      }
  
      alert('Obrigado por participar da pesquisa!');

      setTimeout(() => {
        window.location.reload();
      }, 500);
  
    } catch (error) {
      console.error('Erro ao salvar respostas: ', error);
      alert('Ocorreu um erro ao salvar o questionário. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getUserIP = async () => {
    const res = await fetch('https://api.ipify.org?format=json');
    const data = await res.json();
    return data.ip;
  };

  const getUserLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        resolve({
          latitude: 0,
          longitude: 0,
          city: 'Não disponível',
          state: '',
          country: '',
          address: 'Geolocalização não suportada'
        });
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          
          try {
            // Usando Nominatim (OpenStreetMap) para geocodificação reversa - gratuito
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&accept-language=pt-BR`
            );
            const data = await response.json();
            
            const address = data.address || {};
            resolve({
              latitude,
              longitude,
              city: address.city || address.town || address.village || address.municipality || 'Desconhecida',
              state: address.state || '',
              country: address.country || '',
              address: data.display_name || 'Endereço não disponível'
            });
          } catch (error) {
            console.error('Erro ao obter endereço:', error);
            resolve({
              latitude,
              longitude,
              city: 'Não disponível',
              state: '',
              country: '',
              address: 'Erro ao obter endereço'
            });
          }
        },
        (error) => {
          console.error('Erro ao obter localização:', error);
          resolve({
            latitude: 0,
            longitude: 0,
            city: 'Não disponível',
            state: '',
            country: '',
            address: 'Permissão de localização negada'
          });
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    });
  };

  if (!questionario) {
    return <p>Carregando questionário...</p>;
  }

  const questionnaireName = questionario.nome ?? questionario.name;

  if (questionario.suspenso && !isPreviewMode) {
    return (
      <div className="container-responder survey-start-screen">
        <img
          className="tabulaquest"
          src={`${process.env.PUBLIC_URL}/tabulaquest.jpg`}
          alt="Tabulare"
        />
        <h1>{questionnaireName}</h1>
        <div className="survey-suspended-notice">
          <span className="survey-suspended-icon" aria-hidden>
            ⏸️
          </span>
          <div>
            <strong>Pesquisa disponível somente via aplicativo.</strong>
            <p>Esta pesquisa não está disponível no momento. Somente via aplicativo.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!surveyStarted) {
    return (
      <div className="container-responder survey-start-screen">
        <img
          className="tabulaquest"
          src={`${process.env.PUBLIC_URL}/tabulaquest.jpg`}
          alt="Tabulare"
        />
        <h1>{questionnaireName}</h1>
        <p className="survey-start-lead">
          {isPreviewMode
            ? 'Visualize o questionário antes de compartilhar o link com os entrevistadores.'
            : 'Obrigado por participar desta pesquisa. Quando estiver pronto, clique no botão abaixo para começar.'}
        </p>
        {!isPreviewMode && (
          <div className="survey-start-audio-notice">
            <span className="survey-start-audio-icon" aria-hidden>
              🎙️
            </span>
            <p>
              Ao iniciar, o áudio da entrevista será gravado automaticamente. O navegador solicitará
              permissão para usar o microfone.
            </p>
          </div>
        )}
        {isPreviewMode && (
          <div className="preview-mode-banner survey-start-preview-banner">
            <div className="preview-banner-content">
              <span className="preview-icon">👁️</span>
              <div className="preview-text-content">
                <strong>Modo Visualização</strong>
                <p>Nenhuma resposta ou gravação será salva.</p>
              </div>
            </div>
          </div>
        )}
        <button
          type="button"
          className="btn-nav btn-iniciar-pesquisa"
          onClick={handleStartSurvey}
        >
          {isPreviewMode ? '▶️ Iniciar visualização' : '▶️ Iniciar pesquisa'}
        </button>
      </div>
    );
  }

  const totalQuestions = questionario.questions.length;
  const progress = ((currentQuestion + 1) / totalQuestions) * 100;
  const currentQ = questionario.questions[currentQuestion];

  return (
    <div className="container-responder">
      {isPreviewMode && (
        <div className="preview-mode-banner">
          <div className="preview-banner-content">
            <span className="preview-icon">👁️</span>
            <div className="preview-text-content">
              <strong>Modo Visualização</strong>
              <p>Você pode interagir com o questionário, mas nenhuma resposta será salva.</p>
            </div>
          </div>
        </div>
      )}

      {shouldRecordAudio && (
        <div className={`audio-recording-banner ${recordingError ? 'audio-recording-error' : ''}`}>
          <div className="audio-recording-content">
            <span className="audio-recording-icon" aria-hidden>
              {recordingError ? '⚠️' : '🎙️'}
            </span>
            <div className="audio-recording-text">
              {recordingError ? (
                <>
                  <strong>Áudio indisponível</strong>
                  <p>{recordingError}</p>
                </>
              ) : (
                <>
                  <strong>Entrevista sendo gravada</strong>
                  <p>
                    O áudio desta entrevista será salvo ao finalizar.
                    {isRecording && (
                      <span className="audio-recording-timer">
                        {' '}
                        Tempo: {formatRecordingDuration(elapsedSeconds)}
                      </span>
                    )}
                  </p>
                </>
              )}
            </div>
            {isRecording && !recordingError && (
              <span className="audio-recording-pulse" aria-label="Gravando" />
            )}
          </div>
        </div>
      )}
      
      <img className="tabulaquest" src={`${process.env.PUBLIC_URL}/tabulaquest.jpg`} alt="Descrição da imagem" />
      <h1>{questionnaireName}</h1>
      <h3>{isPreviewMode ? 'Preview do Questionário' : 'Grato por responder o questionário abaixo'}</h3>
      
      {/* Barra de Progresso */}
      <div style={{ 
        width: '100%', 
        backgroundColor: '#f5f5f5', 
        borderRadius: '10px', 
        marginBottom: '15px',
        overflow: 'hidden',
        height: '12px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1) inset'
      }}>
        <div style={{ 
          width: `${progress}%`, 
          height: '100%', 
          background: 'linear-gradient(90deg, #4CAF50 0%, #66BB6A 100%)',
          transition: 'width 0.5s ease',
          borderRadius: '10px',
          boxShadow: '0 2px 4px rgba(76, 175, 80, 0.3)'
        }}></div>
      </div>
      <div style={{ textAlign: 'center', marginBottom: '25px' }}>
        <span style={{ 
          fontWeight: '600',
          fontSize: '15px',
          color: '#555',
          backgroundColor: '#f0f0f0',
          padding: '8px 16px',
          borderRadius: '20px',
          display: 'inline-block'
        }}>
          📝 Pergunta {currentQuestion + 1} de {totalQuestions}
        </span>
      </div>

      {/* Pergunta Atual */}
      <div className="question-block">
        {currentQ.tituloBloco?.trim() && (
          <h2 className="question-block-title">{currentQ.tituloBloco.trim()}</h2>
        )}
        <p>
          {currentQ.question}
          {currentQ.required && <span style={{ color: 'red', marginLeft: '5px' }}>*</span>}
        </p>
        {!currentQ.required && (
          <p style={{ fontSize: '14px', color: '#666', fontStyle: 'italic', marginBottom: '10px' }}>
            (Opcional)
          </p>
        )}
        {currentQ.type === 'text' ? (
          <input
            type="text"
            value={respostas[currentQuestion]}
            onChange={(e) => handleChange(currentQuestion, e.target.value)}
            placeholder={currentQ.required ? "Digite sua resposta aqui..." : "Digite sua resposta aqui (opcional)..."}
          />
        ) : currentQ.type === 'matrix' ? (
          <div className="matrix-question-wrapper">
            <table className="matrix-question-table">
              <thead>
                <tr>
                  <th scope="col" className="matrix-corner-cell" />
                  {(currentQ.options || []).map((option, oIndex) => {
                    const optionText = typeof option === 'string' ? option : option.text || '';
                    return (
                      <th key={oIndex} scope="col" className="matrix-col-header">
                        {oIndex + 1}. {optionText}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {(currentQ.matrixRows && currentQ.matrixRows.length ? currentQ.matrixRows : ['']).map((rowLabel, rowIndex) => {
                  const ans = respostas[currentQuestion];
                  const rowVal =
                    ans && typeof ans === 'object' && !Array.isArray(ans)
                      ? ans[rowIndex] ?? ans[String(rowIndex)]
                      : undefined;
                  return (
                    <tr key={rowIndex}>
                      <th scope="row" className="matrix-row-header">
                        {rowIndex + 1}. {rowLabel}
                      </th>
                      {(currentQ.options || []).map((option, colIndex) => {
                        const optionText = typeof option === 'string' ? option : option.text || '';
                        return (
                          <td key={colIndex} className="matrix-cell">
                            <input
                              type="radio"
                              name={`q${currentQuestion}_row${rowIndex}`}
                              id={`q${currentQuestion}_r${rowIndex}_c${colIndex}`}
                              checked={rowVal === colIndex}
                              onChange={() =>
                                handleMatrixCellChange(currentQuestion, rowIndex, colIndex)
                              }
                              className="radio-custom"
                              aria-label={`${rowLabel}: ${optionText}`}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : currentQ.type === 'checkbox' ? (
          <div className="checkbox-answer-group">
            {(currentQ.options || []).map((option, oIndex) => {
              const optionText = typeof option === 'string' ? option : option.text ?? '';
              const selected = respostas[currentQuestion];
              const checked = Array.isArray(selected) && selected.includes(optionText);
              return (
                <div key={oIndex} className="checkbox-answer-option">
                  <input
                    type="checkbox"
                    id={`q${currentQuestion}_cb${oIndex}`}
                    checked={checked}
                    onChange={(e) =>
                      handleCheckboxChange(currentQuestion, optionText, e.target.checked)
                    }
                    className="checkbox-answer-input"
                  />
                  <label htmlFor={`q${currentQuestion}_cb${oIndex}`}>{optionText}</label>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="radio-group">
            {(currentQ.options || []).map((option, oIndex) => {
              // Garante compatibilidade com formato antigo (string) e novo (objeto)
              const optionText = typeof option === 'string' ? option : option.text;
              const skipTo = typeof option === 'string' ? null : option.skipTo;
              
              return (
                <div key={oIndex} className="radio-option">
                  <input
                    type="radio"
                    id={`q${currentQuestion}_o${oIndex}`}
                    name={`q${currentQuestion}`}
                    value={optionText}
                    checked={respostas[currentQuestion] === optionText}
                    onChange={() => handleChange(currentQuestion, optionText)}
                    className="radio-custom"
                  />
                  <label htmlFor={`q${currentQuestion}_o${oIndex}`}>
                    {optionText}
                    {skipTo && (
                      <span style={{ fontSize: '11px', color: '#888', marginLeft: '8px' }}>
                        {skipTo === 'end' ? '(Finaliza)' : `(Pula para P${skipTo + 1})`}
                      </span>
                    )}
                  </label>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Botões de Navegação */}
      <div className="navigation-buttons-container">
        <button 
          type="button" 
          onClick={handleCancel}
          className="btn-nav btn-cancelar"
        >
          ❌ Cancelar
        </button>
        
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', flex: '1 1 auto', justifyContent: 'flex-end' }}>
          {currentQuestion > 0 && (
            <button 
              type="button" 
              onClick={handlePrevious}
              className="btn-nav btn-anterior"
            >
              ⬅️ Anterior
            </button>
          )}
          
          {currentQuestion < totalQuestions - 1 ? (
            <button 
              type="button" 
              onClick={handleNext}
              className="btn-nav btn-continuar"
            >
              Continuar ➡️
            </button>
          ) : (
            <button 
              type="button" 
              onClick={handleSubmit}
              className="btn-nav btn-finalizar"
              disabled={isSubmitting}
            >
              {isSubmitting ? '⏳ Enviando...' : '✅ Finalizar'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResponderQuestionario;
